import { useEffect, useContext, useState, useRef } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import Map from "ol/Map";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import View from "ol/View";
import { GeoJSON, WKT } from "ol/format";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { cloneDeep } from "lodash";
import { useSelector } from "react-redux";
import store from "../../stores/store";
import Editors from "../editors/editors";
import DownloadFeatures from "../controls/downloadFeatures";
import { trans } from "../../lib/utils";

function FeatureDetails(props) {
  const [selectedProjection, setSelectedProjection] = useState("EPSG:4326");

  const sources = useSelector((state) => store.getState().sources);
  const layers = useSelector((state) => store.getState().layers);

  const [layer] = useState(layers[props.feature.layerId]);

  const [source] = useState(sources[layer.source.id]);

  const [feature] = useState(props.feature);

  const mapRef = useRef();

  useEffect(() => {
    const featureMap = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM({ singleTile: true, ratio: 1 }),
        }),
      ],
      view: new View({
        projection: "EPSG:4326",
        center: [23.704764001367, 38.002281696053],
        zoom: 6,
      }),
    });

    const json = new GeoJSON({
      dataProjection: feature.projection,
      featureProjection: featureMap.getView().getProjection().getCode(),
    }).readFeature(feature);

    const vectorSource = new VectorSource({
      features: [json],
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource,
    });

    featureMap.addLayer(vectorLayer);

    featureMap.getView().fit(vectorSource.getExtent());
  }, []);

  function GeoJSONToWKT(feature, toProjection) {
    const json = new GeoJSON({
      dataProjection: feature.projection ? feature.projection : "EPSG:4326",
      featureProjection: toProjection,
    }).readFeature(feature);

    const wkt = new WKT().writeFeature(json);
    return wkt;
  }

  return (
    <div
      className=" w-100 p-2 h-100"
      style={{ minHeight: "calc(100vh - 15rem)" }}
    >
      <div className="d-flex">
        <div className="w-100">
          <label className="color-get-blue strong small">
            {trans("Layer Name")}
          </label>
          <div className="mt-2 small">{layer?.title}</div>
        </div>
        <div>
          <button
            className="btn btn-sm btn-link border small m-1"
            onClick={() => {
              window["secondaryModal"].setState({
                show: true,
                title: trans("Download features"),
                size: "default",
                body: (
                  <DownloadFeatures
                    layer={layer.id}
                    features={[
                      feature._featureId ? feature._featureId : feature.id,
                    ]}
                  ></DownloadFeatures>
                ),
              });
            }}
          >
            {trans("Download")}
          </button>
        </div>
      </div>
      <ul className="nav nav-tabs navbar-expand small">
        <li className="nav-item " role="button">
          <span
            className={"nav-link color-strong-grey nav-ellipsis active"}
            data-bs-toggle="tab"
            data-bs-target={"#feature-info-overview"}
          >
            <span className="color-black">{trans("Overview")}</span>
          </span>
        </li>
        <li className="nav-item " role="button">
          <span
            className={"nav-link color-strong-grey nav-ellipsis "}
            data-bs-toggle="tab"
            data-bs-target={"#feature-info-geometry"}
          >
            <span className="color-black">{trans("Geometry (WKT)")}</span>
          </span>
        </li>
        <li className="nav-item " role="button">
          <span
            className={"nav-link color-strong-grey nav-ellipsis "}
            data-bs-toggle="tab"
            data-bs-target={"#feature-info-source"}
          >
            <span className="color-black">{trans("Source info")}</span>
          </span>
        </li>
      </ul>
      <div className="tab-content  h-100 ">
        <div
          className={"tab-pane fade  active show h-100  "}
          id={"feature-info-overview"}
          role="tabpanel"
          aria-labelledby={"feature-info-overview"}
        >
          <div
            ref={mapRef}
            className="map-wrapper mt-3"
            style={{ position: "relative", width: "100%", height: "20rem" }}
          ></div>
          <div className="mt-3">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th colSpan={2}>{trans("Feature properties")}</th>
                </tr>
              </thead>
              <tbody>
                {layer?.attributes.map((el) => {
                  if (el.visible) {
                    return (
                      <tr>
                        <th>{el.title ? el.title : el.name}</th>
                        <td>{Editors(feature.properties[el.name], el.type)}</td>
                      </tr>
                    );
                  }
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div
          className={"tab-pane fade  h-100n "}
          id={"feature-info-geometry"}
          role="tabpanel"
          aria-labelledby={"feature-info-geometry"}
        >
          {feature ? (
            <div className="mt-3">
              <label className="color-get-blue strong ">
                {trans("Select projection system")}
              </label>
              <select
                className="form-select form-select-sm "
                value={selectedProjection}
                onChange={(e) => {
                  setSelectedProjection(e.target.value);
                }}
              >
                {window["appConfig"]()["projectionSystems"].map((opt) => {
                  return <option value={opt.code}>{opt.title}</option>;
                })}
              </select>
              <div className="p-2 border mt-3 bg-light small">
                {GeoJSONToWKT(feature, selectedProjection)}
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
        <div
          className={"tab-pane fade  h-100n "}
          id={"feature-info-source"}
          role="tabpanel"
          aria-labelledby={"feature-info-source"}
        >
          <div className="mb-3 mt-3">
            <label className="color-get-blue strong ">
              {trans("Layer Name")}
            </label>
            <div className="mt-2">{layer?.title}</div>
            <div className="mt-1">
              <span className="text-secondary">{layer?.name}</span>
            </div>
          </div>
          <div className="mb-3">
            <label className="color-get-blue strong ">
              {trans("Service type")}
            </label>
            <div className="mt-2">{source.type}</div>
          </div>
          <div className="mb-3">
            <label className="color-get-blue strong ">
              {trans("Service URL")}
            </label>
            <div className="mt-2">
              {source.type === "GEOJSON" ? source.name : source.url}
            </div>
          </div>
          <div className="mb-3">
            <label className="color-get-blue strong ">
              {trans("Layer Abastract")}
            </label>
            <div className="mt-2">{layer?.abstract}</div>
          </div>

          <div className="mb-3">
            <label className="color-get-blue strong ">
              {trans("Layer Legend")}
            </label>
            <div className="mt-2">
              <img src={layer.legend} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default FeatureDetails;
